import { useMemo, useCallback, useReducer } from 'react';
import { MediaItemValue } from '@fulcrumapp/fulcrum-core';
import type { Image, ModalProps } from '../editor/fields/media-props';

interface MediaFieldValue {
  items: Image[];
  addItem(accessKey: string, name?: string): void;
  removeItem(mediaID: string): void;
}

interface MediaFieldAdapterParams<TCallbackValue = MediaFieldValue> {
  value: MediaFieldValue;
  rawValue?: TCallbackValue;
  feature: object;
  record: object;
  getThumbnailURL: (item: Image) => string;
  onFieldChange: (value: TCallbackValue) => void;
  onMediaAdd?: (
    value: TCallbackValue,
    metadata: Record<string, unknown>,
    callback: () => void,
  ) => void;
  onMediaRemove?: (
    value: TCallbackValue,
    info: { id: string },
  ) => void;
  overrideMediaGalleryEnabled: boolean | null;
  isMediaGalleryEnabled: boolean;
}

interface MediaFieldAdapterResult {
  images: Image[];
  handleAdd: (metadata: Record<string, unknown>, uploadedFile: { access_key: string; name?: string }) => void;
  handleRemove: (mediaID: string) => void;
  handleCaptionChange: (item: Image, newCaption: string) => void;
  isGalleryEnabled: boolean;
  modalProps: ModalProps;
}

/**
 * Bridges Element-coupled data from the legacy editor (element-list-view)
 * into Element-agnostic props consumable by media-field, sketch-field,
 * and future function-based media fields.
 *
 * Responsibilities:
 * - Hydrates raw media items with thumbnailURL, onDelete, onCaptionChange
 * - Encapsulates add/delete flows (including the onMediaAdd callback-in-callback)
 * - Resolves gallery-enabled override logic
 * - Assembles modalProps for legacy thumbnail galleries
 */
function useMediaFieldAdapter<TCallbackValue = MediaFieldValue>({
  value,
  rawValue,
  feature,
  record,
  getThumbnailURL,
  onFieldChange,
  onMediaAdd,
  onMediaRemove,
  overrideMediaGalleryEnabled,
  isMediaGalleryEnabled,
}: MediaFieldAdapterParams<TCallbackValue>): MediaFieldAdapterResult {
  // The callbacks from editor.tsx expect the original formValue (which has
  // .element), not the wrapped MediaFieldValue. Pass rawValue when available.
  const callbackValue = (rawValue ?? value) as TCallbackValue;

  // fulcrum-core values are mutated in place — reference identity never
  // changes after addItem / removeItem. Incrementing a revision counter
  // after each mutation busts the useMemo that hydrates `images`, ensuring
  // React re-renders with the current item list.
  const [revision, bumpRevision] = useReducer((n: number) => n + 1, 0);

  const handleDelete = useCallback((mediaID: string) => {
    value.removeItem(mediaID);
    bumpRevision();
    onFieldChange(callbackValue);
    onMediaRemove?.(callbackValue, { id: mediaID });
  }, [value, callbackValue, onFieldChange, onMediaRemove]);

  const handleCaptionChange = useCallback((item: Image, newCaption: string) => {
    const original = value.items.find((i: Image) => i.mediaID === item.mediaID);

    if (original) {
      original.caption = newCaption;
    }

    bumpRevision();
    onFieldChange(callbackValue);
  }, [value, callbackValue, onFieldChange]);

  // Intentional mutation: deprecated-captioned-thumbnail writes caption
  // changes back into these objects (item.caption = newValue), so they
  // must be the same references that live in value.items.
  // Per-item onCaptionChange binds the item for legacy consumers that call
  // onCaptionChange(newCaption) directly on the image object.
  const images = useMemo(() => {
    const { items } = value;

    return items.map((item: Image) => {
      const hydrated = item;
      hydrated.thumbnailURL = getThumbnailURL(item);
      hydrated.onDelete = handleDelete;
      hydrated.onCaptionChange = (newCaption: string) => handleCaptionChange(item, newCaption);
      return hydrated;
    });
  }, [value.items, getThumbnailURL, handleDelete, handleCaptionChange, revision]);

  const handleAdd = useCallback(
    (metadata: Record<string, unknown>, uploadedFile: { access_key: string; name?: string }) => {
      const commit = () => {
        const accessKey = uploadedFile?.access_key;

        if (!accessKey) {
          return;
        }

        value.addItem(accessKey, uploadedFile?.name);
        bumpRevision();
        onFieldChange(callbackValue);
      };

      if (onMediaAdd) {
        onMediaAdd(callbackValue, metadata, commit);
      } else {
        commit();
      }
    },
    [value, callbackValue, onMediaAdd, onFieldChange],
  );

  const galleryEnabled = overrideMediaGalleryEnabled ?? isMediaGalleryEnabled;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- revision busts the memo after in-place mutations
  const modalProps: ModalProps = useMemo(() => ({
    record,
    feature,
    mediaItems: value.items as unknown as MediaItemValue[],
  }), [record, feature, value.items, revision]);

  return {
    images,
    handleAdd,
    handleRemove: handleDelete,
    handleCaptionChange,
    isGalleryEnabled: galleryEnabled,
    modalProps,
  };
}

export { useMediaFieldAdapter };
export type { MediaFieldAdapterParams, MediaFieldAdapterResult, MediaFieldValue };
